/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { LayoutTemplate, Plus, Target } from 'lucide-react';
import { type ChangeEvent, type JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Pagination } from '../../components/ui/pagination.js';
import { ProgressBar } from '../../components/ui/progress-bar.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type PlanListItem, type PlansPageParams } from '../../lib/api/itp.js';
import { useClients } from '../../lib/queries/clinical.js';
import { usePlansPage } from '../../lib/queries/itp.js';
import { initialsOf } from '../../lib/utils/initials.js';

import { PlanDetailModal } from './plan-detail-modal.js';
import { PlanForm } from './plan-form.js';
import { ReviewsDueBanner } from './reviews-due-banner.js';
import { TemplatesModal } from './templates-modal.js';

const MAX_PILLS = 3;
const PAGE_LIMIT = 20;

/** Build the server query params from the page's search state. */
function pageParams(offset: number, q: string): PlansPageParams {
  return { limit: PAGE_LIMIT, offset, ...(q !== '' ? { q } : {}) };
}

function GoalPills({ titles }: { readonly titles: readonly string[] }): JSX.Element {
  const { t } = useTranslation();
  const shown = titles.slice(0, MAX_PILLS);
  const extra = titles.length - shown.length;
  return (
    <div className="flex flex-wrap gap-2">
      {shown.map((title) => (
        <span
          key={title}
          className="inline-flex items-center gap-1.5 rounded-full bg-[var(--accent-soft)] px-3 py-1 text-xs font-medium text-[var(--accent-strong)]"
        >
          <Target className="h-3 w-3" aria-hidden="true" />
          {title}
        </span>
      ))}
      {extra > 0 && (
        <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500 dark:bg-slate-700 dark:text-slate-300">
          {t('therapist.plans.moreGoals', { count: extra })}
        </span>
      )}
    </div>
  );
}

interface PlanCardProps {
  readonly plan: PlanListItem;
  readonly clientName: string;
  readonly onViewDetails: (id: string) => void;
}

function PlanCard({ plan, clientName, onViewDetails }: PlanCardProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const view = useCallback(() => {
    onViewDetails(plan.id);
  }, [onViewDetails, plan.id]);

  const update = useCallback(() => {
    void navigate(`/therapist/plans/${plan.id}`);
  }, [navigate, plan.id]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-sm font-bold text-white">
            {initialsOf(clientName)}
          </span>
          <div>
            <p className="font-semibold text-slate-800 dark:text-slate-100">{clientName}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">{plan.name}</p>
          </div>
        </div>
        {plan.reviewDate !== null && (
          <div className="text-right">
            <p className="text-xs text-slate-400">{t('therapist.plans.reviewDate')}</p>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {plan.reviewDate}
            </p>
          </div>
        )}
      </div>

      <div className="mt-5">
        <div className="mb-1.5 flex justify-between text-sm">
          <span className="text-slate-500 dark:text-slate-400">
            {t('therapist.plans.progress')}
          </span>
          <span className="font-semibold text-slate-700 dark:text-slate-200">
            {plan.progressPct}%
          </span>
        </div>
        <ProgressBar value={plan.progressPct} />
      </div>

      {plan.goalTitles.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 text-sm font-medium text-slate-600 dark:text-slate-300">
            {t('therapist.plans.currentGoals')}
          </p>
          <GoalPills titles={plan.goalTitles} />
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 gap-3">
        <Button variant="outline" onClick={view}>
          {t('therapist.plans.viewDetails')}
        </Button>
        <Button onClick={update}>{t('therapist.plans.updateProgress')}</Button>
      </div>
    </div>
  );
}

function PlansSearch({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const onInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );
  return (
    <input
      type="search"
      value={value}
      onChange={onInput}
      placeholder={t('therapist.plans.searchPlaceholder')}
      aria-label={t('therapist.plans.searchPlaceholder')}
      className="focus-ring mb-4 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
    />
  );
}

/** Therapist → Therapy Plans (Figma). Rich plan cards + read-only detail modal. */
export function PlansPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [debouncedQ, setDebouncedQ] = useState('');
  const plans = usePlansPage(pageParams(offset, debouncedQ));
  const clients = useClients();
  const [detailId, setDetailId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);

  // Debounce the search box into the server query; reset to page 1 on change.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQ(search.trim());
      setOffset(0);
    }, 300);
    return () => {
      clearTimeout(id);
    };
  }, [search]);

  const clientNames = useMemo(
    () => new Map((clients.data ?? []).map((c) => [c.id, c.fullName])),
    [clients.data],
  );

  const closeDetail = useCallback(() => {
    setDetailId(null);
  }, []);
  const openNew = useCallback(() => {
    setNewOpen(true);
  }, []);
  const openTemplates = useCallback(() => {
    setTemplatesOpen(true);
  }, []);
  const onCreated = useCallback(
    (planId: string) => {
      setNewOpen(false);
      void navigate(`/therapist/plans/${planId}`);
    },
    [navigate],
  );

  const list = plans.data?.items ?? [];
  const total = plans.data?.total ?? 0;
  const detailClient =
    detailId !== null
      ? (clientNames.get(list.find((p) => p.id === detailId)?.clientId ?? '') ?? '—')
      : '—';

  return (
    <div className="p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <PageHeader
          title={t('therapist.plans.title')}
          subtitle={t('therapist.plans.count', { count: total })}
        />
        <div className="flex gap-2">
          <Button variant="outline" onClick={openTemplates}>
            <LayoutTemplate className="h-4 w-4" aria-hidden="true" />
            {t('therapist.templates.title')}
          </Button>
          <Button onClick={openNew}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('therapist.planForm.newPlan')}
          </Button>
        </div>
      </div>
      <ReviewsDueBanner />
      <PlansSearch value={search} onChange={setSearch} />
      <QueryState
        isLoading={plans.isLoading}
        isError={plans.isError}
        isEmpty={list.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('therapist.plans.empty')}
      >
        <div className="grid gap-5 lg:grid-cols-2">
          {list.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              clientName={clientNames.get(plan.clientId) ?? '—'}
              onViewDetails={setDetailId}
            />
          ))}
        </div>
        <Pagination total={total} limit={PAGE_LIMIT} offset={offset} onOffsetChange={setOffset} />
      </QueryState>

      <PlanDetailModal planId={detailId} clientName={detailClient} onClose={closeDetail} />

      <Modal
        open={newOpen}
        onOpenChange={setNewOpen}
        title={t('therapist.planForm.newPlan')}
        description={t('therapist.planForm.subtitle')}
        closeLabel={t('common.cancel')}
      >
        <PlanForm onDone={onCreated} />
      </Modal>

      <TemplatesModal open={templatesOpen} onOpenChange={setTemplatesOpen} />
    </div>
  );
}
